<template>
  <section class="about-me">
    <div class="wrapper">
      <!-- Centralny element Frontend -->
      <div class="central-element">
        <h1 class="central-icon">{{ $t('info.knowledge.title') }}</h1>
        <!-- Orbitujące ikony wokół Frontend -->
        <div
          v-for="(icon, index) in frontendOrbitingIcons"
          :key="index"
          class="orbit-container"
          :ref="el => { if (el) frontendIconRefs[index] = el }"
          :style="getOrbitContainerStyle(icon.radius)"
        >
          <img
            :src="icon.src"
            :alt="icon.alt"
            class="tech-icon"
            :style="getTechIconStyle(icon.radius)"
          />
        </div>
      </div>
    </div>
    <div class="about-me-content">
      <div class="info-boxes">
    <div class="info-box one">
      <h3>{{ $t('info.experience.title') }}</h3>
      <p>{{ $t('info.experience.years') }}</p>
      <p>{{ $t('info.experience.description') }}</p>
    </div>
    <div class="info-box two">
      <h3>{{ $t('info.skills.title') }}</h3>
      <p>{{ $t('info.skills.list') }}</p>
      <p>{{ $t('info.skills.description') }}</p>
    </div>
    <div class="info-box three">
      <h3>{{ $t('info.education.title') }}</h3>
      <p>{{ $t('info.education.degree') }}</p>
      <p>{{ $t('info.education.description') }}</p>
    </div>
    <div class="info-box four">
      <h3>{{ $t('info.interests.title') }}</h3>
      <p>{{ $t('info.interests.list') }}</p>
      <p>{{ $t('info.interests.description') }}</p>
    </div>
  </div>
    </div>
  </section>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import gsap from 'gsap';


// Definiowanie ikon orbitujących dla Frontend
const frontendOrbitingIcons = [
  { src: 'js.png', alt: 'JavaScript', radius: 100, speed: 40 },
  { src: 'python.svg', alt: 'Python', radius: 150, speed: -30 },
  { src: 'scss.png', alt: 'SCSS', radius: 200, speed: -25 },
  { src: 'next.svg', alt: 'Next.js', radius: 250, speed: 20 },
  { src: 'openai.svg', alt: 'OpenAI', radius: 300, speed: 25 },
  { src: 'flask.png', alt: 'Flask', radius: 350, speed: 10 },
  { src: 'chromaDB.webp', alt: 'ChromaDB', radius: 400, speed: -15 },
  { src: 'nuxt.svg', alt: 'Nuxt.js', radius: 450, speed: 30 },
  { src: 'shopware.svg', alt: 'Shopware', radius: 500, speed: 18 },
  { src: 'vue.svg', alt: 'Vue.js', radius: 550, speed: -20 },
];

// Definiowanie ikon orbitujących dla Backend
const backendOrbitingIcons = [
];

// Refs
const frontendIconRefs = ref([]);
const backendIconRefs = ref([]);
const frontendIconRef = ref(null);
const backendIconRef = ref(null);

// Funkcje pomocnicze do stylów pozycji
const getOrbitContainerStyle = (radius) => ({
  width: `${radius * 2}px`,
  height: `${radius * 2}px`,
  marginLeft: `-${radius}px`,
  marginTop: `-${radius}px`,
});

const getTechIconStyle = (radius) => ({
  top: '0',
  left: `${radius}px`,
});

onMounted(() => {
  // Animacja centralnej ikony Frontend
  gsap.to(frontendIconRef.value, {
    rotation: '+=360',
    duration: 0,
    repeat: -1,
    ease: 'linear',
    transformOrigin: 'center center',
  });

  // Animacja centralnej ikony Backend
  gsap.to(backendIconRef.value, {
    rotation: '+=360',
    duration: 0,
    repeat: -1,
    ease: 'linear',
    transformOrigin: 'center center',
  });

  // Animacja ikon orbitujących Frontend
  frontendIconRefs.value.forEach((iconContainer, index) => {
    const { speed } = frontendOrbitingIcons[index];
    gsap.to(iconContainer, {
      rotation: speed > 0 ? '+=360' : '-=360',
      duration: 360 / Math.abs(speed),
      repeat: -1,
      ease: 'linear',
      transformOrigin: 'center center',
    });
  });

  // Animacja ikon orbitujących Backend
  backendIconRefs.value.forEach((iconContainer, index) => {
    const { speed } = backendOrbitingIcons[index];
    gsap.to(iconContainer, {
      rotation: speed > 0 ? '+=360' : '-=360',
      duration: 360 / Math.abs(speed),
      repeat: -1,
      ease: 'linear',
      transformOrigin: 'center center',
    });
  });
});
</script>

<style scoped>
.about-me-content {
  position: absolute;
  top: 20%;
  left: 50%;
  transform: translate(-50%, -50%);
} 
.about-me {
  min-height: 100vh;
  height: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  background-color: #0a0a0a;
  color: white;
  position: relative;
  overflow: hidden;
}

.wrapper {
  display: flex;
  justify-content: space-around;
  width: 100%;
}

.central-element {
  position: relative;
  width: 300px;
  height: 300px;
}

.central-icon {
  object-fit: contain;
  position: absolute;
  text-align: center;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}

.orbit-container {
  position: absolute;
  opacity: 0.5;
  top: 50%;
  left: 50%;
}

.orbit-container::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: conic-gradient(
    from 354deg,
    rgba(255, 255, 255, 0.),
    rgba(255, 255, 255, 0) 10%
  );
  transform: rotate(0deg);
  animation: orbitTrail 20s linear infinite;
}

.tech-icon {
  width: 30px;
  height: 30px;
  object-fit: contain;
  position: absolute;
}

.info-boxes {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10rem;
  position: relative;
  z-index: 2;
  margin-bottom: 2rem;
}

.info-box {
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 1.5rem;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  transition: transform 0.3s ease, box-shadow 0.3s ease;
}

.info-box:hover {
  transform: translateY(-5px);
  box-shadow: 0 6px 8px rgba(0, 0, 0, 0.2);
}

.info-box h3 {
  font-size: 1.2rem;
  margin-bottom: 0.5rem;
  color: #ff3366;
}

.info-box p {
  font-size: 0.9rem;
  line-height: 1.4;
}

.about-me-content {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
  z-index: 2;
}

.about-me-content h2 {
  font-size: 2rem;
  margin-bottom: 1rem;
  color: #ff3366;
}

.about-me-content p {
  font-size: 1rem;
  line-height: 1.6;
}

@media (max-width: 950px) {
  .info-boxes {
    grid-template-columns: 1fr;
  }
}
</style>
